import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Button, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import type { SignInIdentity, SignInProvider } from 'fym-shared/src/music24/signInMethods';
import { TypedMemo } from '../../../../common/types';
import { ConfirmDialog } from '../../../../common/views/modals/confirmDialog/ConfirmDialog';
import { useConfirmWhileBusy } from '../../../../common/views/modals/confirmDialog/useConfirmWhileBusy';
import { signInMethodsMessages } from './signInMethodsMessages';
import { SignInProviderMark } from './SignInProviderMark';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import cards from '../../settingsCards.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the sign-in methods card; each part uses its own slice of it */
import styles from './signInMethods.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface Props {
  provider: SignInProvider;
  identity: SignInIdentity | null;
  /** The Connect button shows (the keys exist, not in the native web view). */
  canConnect: boolean;
  isReady: boolean;
  isConnecting: boolean;
  isDisconnecting: boolean;
  onConnect: (provider: SignInProvider) => void;
  onDisconnect: (identityId: number) => void;
}

/** One provider row (design sign-in-methods a1, a2): the provider tile, the name with the "Connected" pill and the provider email, and Disconnect or Connect. */
const SignInProviderRowComponent: React.FC<Props> = ({
  provider,
  identity,
  canConnect,
  isReady,
  isConnecting,
  isDisconnecting,
  onConnect,
  onDisconnect,
}) => {
  const intl = useIntl();
  const name = intl.formatMessage(provider === 'apple' ? signInMethodsMessages.apple : signInMethodsMessages.google);
  const connect = useCallback(() => onConnect(provider), [onConnect, provider]);
  const identityId = identity?.id;
  const disconnect = useCallback(() => {
    if (identityId !== undefined) {
      onDisconnect(identityId);
    }
  }, [identityId, onDisconnect]);
  const dialog = useConfirmWhileBusy({ isBusy: isDisconnecting, onConfirm: disconnect });

  if (identity === null && !canConnect) {
    return null;
  }

  let detail: string;
  if (identity === null) {
    detail = intl.formatMessage(signInMethodsMessages.notConnected);
  } else if (identity.isPrivateRelay) {
    detail = intl.formatMessage(signInMethodsMessages.connectedRelay, { email: identity.email });
  } else {
    detail = intl.formatMessage(signInMethodsMessages.connectedAs, { email: identity.email });
  }

  return (
    <div className={cards.row}>
      <span className={`${styles.providerTile} ${provider === 'apple' ? styles.apple : styles.google}`}>
        <SignInProviderMark provider={provider} />
      </span>
      <div className={cards.rowText}>
        <span className={styles.titleLine}>
          <span className={cards.rowTitle}>{name}</span>
          {identity !== null && (
            <span className={`${cards.pill} ${cards.pillPos}`}>
              <FontAwesomeIcon icon={faCheck} className={styles.pillIcon} />
              {intl.formatMessage(signInMethodsMessages.connected)}
            </span>
          )}
        </span>
        <span className={cards.rowSub} data-clarity-mask="true">
          {detail}
        </span>
        {identity !== null && !identity.canDisconnect && (
          <span className={cards.rowSub}>{intl.formatMessage(signInMethodsMessages.lastMethod)}</span>
        )}
      </div>
      <div className={cards.rowEnd}>
        {identity !== null ? (
          <>
            <Button
              color="secondary"
              outline
              size="sm"
              onClick={dialog.open}
              disabled={!identity.canDisconnect || isDisconnecting}
            >
              {intl.formatMessage(signInMethodsMessages.disconnect)}
            </Button>
            <ConfirmDialog
              isOpen={dialog.isOpen}
              tone="danger"
              title={intl.formatMessage(signInMethodsMessages.disconnectTitle, { provider: name })}
              description={
                <span data-clarity-mask="true">
                  {intl.formatMessage(signInMethodsMessages.disconnectDescription, {
                    provider: name,
                    email: identity.email,
                  })}
                </span>
              }
              cancelLabel={intl.formatMessage(signInMethodsMessages.disconnectCancel)}
              confirmLabel={intl.formatMessage(signInMethodsMessages.disconnect)}
              isBusy={dialog.isRunning}
              onConfirm={dialog.confirm}
              onCancel={dialog.cancel}
            />
          </>
        ) : (
          <Button color="primary" size="sm" onClick={connect} disabled={!isReady || isConnecting} aria-busy={!isReady}>
            {isConnecting && <Spinner size="sm" className="me-2" aria-hidden="true" />}
            {intl.formatMessage(isConnecting ? signInMethodsMessages.connecting : signInMethodsMessages.connect)}
          </Button>
        )}
      </div>
    </div>
  );
};

export const SignInProviderRow = TypedMemo(SignInProviderRowComponent);
